import { Component, AfterViewInit, OnInit } from '@angular/core';
import { DeliveryLocationsJeevesComponent, DeliveryLocationsService } from 'gung-standard-jeeves';
import { SelectedCustomerService, DeliveryDateService, AuthService, CheckoutObject } from 'gung-standard';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { first } from 'rxjs';
import { environment } from './../../../environments/environment';

interface Alert {
  type: string;
  message: string;
}

@Component({
    selector: 'ref-ref-delivery-locations-jeeves',
    templateUrl: './ref-delivery-locations-jeeves.component.html',
    styleUrls: ['./ref-delivery-locations-jeeves.component.scss'],
    standalone: false
})
export class RefDeliveryLocationsJeevesComponent
  extends DeliveryLocationsJeevesComponent
  implements OnInit, AfterViewInit
{
  public loaded = false;
  isUser = false;
  alertMessage: Alert;
  isRef = environment.projectName === 'ref';
  public formOh: FormGroup;

  constructor(
    protected selectedCustomerService: SelectedCustomerService,
    protected deliveryLocationsService: DeliveryLocationsService,
    protected formBuilder: FormBuilder,
    protected datePipe: DatePipe,
    protected deliveryDateService: DeliveryDateService,
    protected authService: AuthService
  ) {
    super(selectedCustomerService, deliveryLocationsService, formBuilder, authService);

    this.authService
      .getCurrentUser()
      .pipe(first())
      .subscribe(user => {
        if (user.roles.includes('USER') && user.roles.length === 1) {
          this.isUser = true;
        }
      });
  }

  ngAfterViewInit(): void {
    this.calculateOrdberlevdat();
  }

  protected handleNextButtonClicked(): void {
    if (!this.loaded) {
      this.alertMessage = {
        message: 'DISPATCH_DATE_REQUIRED',
        type: 'danger'
      };
      return;
    }
    super.handleNextButtonClicked();
  }

  protected initForm() {
    this.form = this.formBuilder.group({
      ordlevadr1: this.formBuilder.control(
        { value: this.checkout.extra.procargs.ordlevadr1 || '', disabled: this.isUser },
        {
          validators: [Validators.required]
        }
      ),
      ordlevadr2: this.formBuilder.control({
        value: this.checkout.extra.procargs.ordlevadr2 || '',
        disabled: this.isUser
      }),
      ordlevadr3: this.formBuilder.control({
        value: this.checkout.extra.procargs.ordlevadr3 || '',
        disabled: this.isUser
      }),
      ftgpostnr: this.formBuilder.control({
        value: this.checkout.extra.procargs.ftgpostnr || '',
        disabled: this.isUser
      }),
      ordlevadrlandskod: this.formBuilder.control({
        value: this.checkout.extra.procargs.ordlevadrlandskod || '',
        disabled: this.isUser
      })
    });

    this.formOh = this.formBuilder.group({
      edit: this.formBuilder.control(this.checkout.extra.oh.edit || '')
    });
  }

  public getBtnTexts(checkout: CheckoutObject): { next: string | void; prev: string | void } | void {
    return {
      next: 'REF_DELIVERY_LOCATION_CHECKOUT_STEP_NEXT_BUTTON',
      prev: null
    };
  }

  calculateOrdberlevdat() {
    const days = 0; // add no bussines days
    this.deliveryDateService
      .getDeliveryDates(this.checkout.extra.procargs.levsattkod)
      .pipe(first())
      .subscribe(dates => {
        let countDays = 0;
        const firstDateAvailable = dates.find(date => {
          return date.valid && countDays++ === days;
        });

        if (firstDateAvailable) {
          this.checkout.extra.oh.ordberlevdat = this.datePipe.transform(firstDateAvailable.date, 'yyyy-MM-dd');
          this.loaded = true;
        } else {
          this.alertMessage = {
            message: 'ERROR_DELIVERY_DATE_UNDEFINED',
            type: 'danger'
          };
        }
      });
  }

  protected afterDeliveryLocationSet(deliveryLocationId: string) {
    const newDeliveryLocation = this.deliveryLocations.find(location => location.id === deliveryLocationId);
    if (!newDeliveryLocation) {
      console.error(new Error(`Could not find delivery location: ${deliveryLocationId}`));
      return;
    }
    this.selectedDeliveryLocation = newDeliveryLocation;

    this.checkout.extra.procargs = this.checkout.extra.procargs || {};
    this.checkout.extra.procargs.ordlevadr1 = this.selectedDeliveryLocation.extra.fr.ftgnamn;
    this.checkout.extra.procargs.ordlevadr2 = this.selectedDeliveryLocation.extra.fr.ftgpostadr1;
    this.checkout.extra.procargs.ordlevadrlandskod = this.selectedDeliveryLocation.extra.fr.landskod;
    this.checkout.extra.procargs.ordlevadrprovincecode = this.selectedDeliveryLocation.extra.fr.provincecode;

    if (
      this.isRef &&
      this.selectedCustomer.extra.fr.ftgpostadr5 &&
      this.selectedCustomer.extra.fr.ftgpostlevadr3 &&
      this.selectedCustomer.extra.fr.ftglevpostnr
    ) {
      this.checkout.extra.procargs.ordlevadr3 = this.selectedDeliveryLocation.extra.fr.ftgpostadr5;
      this.postalCode = this.selectedDeliveryLocation.extra.fr.ftglevpostnr;
      this.city = this.selectedDeliveryLocation.extra.fr.ftgpostlevadr3;
    } else {
      this.checkout.extra.procargs.ordlevadr3 = this.selectedDeliveryLocation.extra.fr.ftgpostadr2;
      this.postalCode = this.selectedDeliveryLocation.extra.fr.ftgpostnr;
      this.city = this.selectedDeliveryLocation.extra.fr.ftgpostadr3;
    }

    this.assignPostalCodeAndCity(this.postalCode, this.city);

    const procargsKeys = Object.keys(this.form.getRawValue());

    const valueMap = {};
    procargsKeys.forEach(key => {
      valueMap[key] = this.checkout.extra.procargs[key] || '';
    });

    this.form.setValue(valueMap);
  }

  protected assignFormValues(form: FormGroup, formOh: FormGroup) {
    super.assignFormValues(form, formOh);

    const rawformOhData = this.formOh.getRawValue();

    this.checkout.extra.oh = {
      ...this.checkout.extra.oh,
      ...rawformOhData
    };
  }
}
